import React, { useState, useMemo } from 'react';
import { typeStyle } from '@carvana/showroom-css-theme';
import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { AnalyticEvents, DateMasks } from '@constants/index';
import styles from './reported-concern.module.css';
import { formatDate } from '../../../../services/UtilitiesService';
import { ConcernDetailsModal } from './concern-details/concern-details';
import type { Concern } from './reported-concern-type';
import { DetailToggle } from '../../../shared/detail-toggle/detail-toggle';
import { SubCategory } from '../../constants';

const getSubcategoryValue = (concern: Concern): string =>
  concern.isLegacy ? concern.category : (concern.subCategory || '');

export const ReportedConcern = ({ reportedConcerns }: { reportedConcerns: Concern[] }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [concern, setConcern] = useState<Concern>({} as Concern);

  const filteredConcerns = useMemo(() => {
    const cosmeticConcerns = reportedConcerns
      .filter((c) => {
        const subcategory = getSubcategoryValue(c);
        return subcategory === SubCategory.Exterior || subcategory === SubCategory.Interior;
      })
      .slice(0, 3);
    
    const glassConcerns = reportedConcerns
      .filter((c) => {
        const subcategory = getSubcategoryValue(c);
        return subcategory === SubCategory.Glass;
      })
      .slice(0, 1);
    
    return [...cosmeticConcerns, ...glassConcerns];
  }, [reportedConcerns]);

  const showConcernModal = (currentConcern: Concern) => {
    setConcern(currentConcern);
    setModalIsOpen(true);
    Analytics.trackEvent(AnalyticEvents.sarSubmittedCasesClickedViewDetails, {
      submissionDate: currentConcern.createdOn,
      concernDetails: currentConcern?.details,
    });
    datadogRum.addAction(AnalyticEvents.sarSubmittedCasesClickedViewDetails, {
      submissionDate: currentConcern.createdOn,
      concernDetails: currentConcern?.details,
    });
  };

  return (
    <div className={styles['reported-concern']}>
      <div className={styles['reported-concern-divider']}> </div>

      <DetailToggle
        id="reported-concern"
        header={`Submitted Cosmetic Concerns (${filteredConcerns.length})`}
        onDetailsToggle={(toggle) => {
          Analytics.trackEvent(
            toggle ? AnalyticEvents.sarExpandedSubmittedCosmeticCase : AnalyticEvents.sarCollapsedSubmittedCosmeticCases
          );
          datadogRum.addAction(
            toggle ? AnalyticEvents.sarExpandedSubmittedCosmeticCase : AnalyticEvents.sarCollapsedSubmittedCosmeticCases
          );
        }}
      >
        <>
          <ConcernDetailsModal modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} cosmeticConcern={concern} />

          <div className={styles['reported-concern-list']}>
            {filteredConcerns.map((concern: Concern, index: number) => {
              const interiorOrExterior = concern?.isLegacy ? concern?.category : concern?.subCategory;

              return (
                <div key={index} className={styles['reported-concern-item']}>
                  <div className={styles['header']} style={typeStyle('header-xs')}>
                    {interiorOrExterior} concern
                  </div>

                  <div className={styles['content']} style={typeStyle('body-s')}>
                    Submitted to SilverRock on {formatDate(concern.createdOn, DateMasks.MMDDYYwSlash, false)}
                  </div>

                  <button
                    className={styles['button']}
                    style={typeStyle('body-s')}
                    type="button"
                    onClick={() => showConcernModal(concern)}
                  >
                    View details
                  </button>
                </div>
              );
            })}
          </div>
        </>
      </DetailToggle>
    </div>
  );
};
